// apps/web/src/pages/spread/BatchSetStrip.tsx
//
// Phase 9 Plan 11 Task 2 — vertical batch-set strip with @dnd-kit reorder.
// Phase 11.7 Plan 03 Task 2 — Surface-2 strip-level additions:
//   - Surface 2d: a strip-level duplicate-name banner (role="alert", warning
//     tone) that appears above the card list when two batch-set names collide
//     after trim + case-normalization, and disappears when they diverge. Pure
//     derived state over the live names (per-path useWatch on batchSets).
//   - Surface 2b: the Duplicate clone wiring — the strip OWNS useFieldArray, so
//     it deep-copies the active row's values (incl. selections +
//     allowSameAcrossBatches) into a fresh append entry and passes an
//     onDuplicate callback down to each BatchSetCard.
//
// Composition:
//   useFieldArray on form path 'batchSets' provides fields/append/remove/move.
//   <SortableList items={fields.map(f => ({id: f.id}))} onReorder={(o,n)=>move(o,n)}>
//     for each field: <SortableItem id={field.id} ...>
//                       <BatchSetCard index={i} onRemove={() => remove(i)}
//                         onDuplicate={() => duplicate(i)} />
//                     </SortableItem>
//   </SortableList>
//
// SECTION HEADER copy per UI-SPEC §"Batch set strip" lines 370-378:
//   - `{subdivision.upper}S` (e.g. `BATCH SETS`) — label typography.
//   - Empty-state copy interpolates useTerm('subdivision') and useTerm('container').
//   - Banner copy interpolates useTerm('subdivision') (Surface 2d).
//
// FIELD-ARRAY ROW KEYS (AUTHR-03): `field.id` always — NEVER `index`.

import { AlertTriangle } from "lucide-react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { SortableItem } from "@/components/layout/SortableItem";
import { SortableList } from "@/components/layout/SortableList";
import { EmptyState } from "@/components/ui/empty-state";
import { Fieldset } from "@/components/ui/fieldset";
import { useTerm } from "@/lib/vocabulary/useTerm";
import { BatchSetCard } from "./BatchSetCard";

// Default values for a brand-new batch set. Mirrors the IngredientGroups
// NEW_GROUP_DEFAULTS pattern (sibling section module). The recipeId is empty
// at append time — Plan 09-12 wires the recipe picker which sets the field
// once the user selects a recipe.
//
// Phase 11.7 Plan 03: the Add CTA seed does NOT seed allowSameAcrossBatches
// (an undefined value reads as Off — the deterministic default) and seeds
// selections to {} (harmless — an empty per-batch scope). The Duplicate path
// (duplicateBatchSet) deep-copies the active row instead, so per-batch state is
// never lost on a clone.
function makeEmptyBatchSet(position: number): {
  id: string;
  name: string;
  recipeId: string;
  batchSize: string;
  position: number;
  selections: Record<string, unknown>;
} {
  // crypto.randomUUID is universally available in Node 22 + every modern
  // browser. jsdom's crypto polyfill in vitest/jest also provides it.
  const id =
    typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : // Fallback for older jsdom builds — Math.random is not cryptographically
        // secure, but for non-secure client-side ids in a brand-new row that
        // gets reassigned on save by the server, it's acceptable. The pnpm/
        // playwright/vitest stack runs on Node 22 with crypto, so this branch
        // is effectively dead code in practice.
        `bs-${Math.random().toString(36).slice(2)}-${position}`;
  return {
    id,
    name: "",
    recipeId: "",
    batchSize: "1x",
    position,
    selections: {},
  };
}

// Normalize a batch-set name for collision detection: trim surrounding
// whitespace + lowercase. Empty/blank names are NOT considered collisions
// (an unnamed row falls back to a positional placeholder in the card, so two
// blank names are not a user-visible duplicate). Returns null for blank.
function normalizeName(name: unknown): string | null {
  if (typeof name !== "string") return null;
  const trimmed = name.trim().toLocaleLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

// Pure: does any pair of names collide after normalization?
function hasDuplicateNames(names: ReadonlyArray<unknown>): boolean {
  const seen = new Set<string>();
  for (const raw of names) {
    const key = normalizeName(raw);
    if (key === null) continue;
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
}

export function BatchSetStrip(): React.ReactElement {
  const { control, getValues } = useFormContext();
  // Default keyName "id" — RHF's stable row id surfaces as field.id. We do
  // NOT override keyName so the per-row React key + SortableItem id stay
  // aligned with the documented AUTHR-03 pattern used across Phase 8.
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "batchSets",
  });
  const subdivision = useTerm("subdivision");
  const container = useTerm("container");

  // Per-path useWatch on the whole batchSets array so the banner re-derives as
  // names mutate. Narrowed to the single 'batchSets' path (NOT a bare
  // useWatch); the derived collision check is cheap (bounded by the field
  // count). AUTHR-06: this is the strip-level subscription the banner needs;
  // per-row name edits already re-render their own card via the card's useWatch.
  const watchedBatchSets = useWatch({ control, name: "batchSets" }) as
    | ReadonlyArray<{ name?: unknown }>
    | undefined;
  const liveNames: ReadonlyArray<unknown> =
    watchedBatchSets !== undefined
      ? watchedBatchSets.map((b) => b?.name)
      : fields.map((f) => (f as { name?: unknown }).name);
  const duplicateNames = hasDuplicateNames(liveNames);

  // Section header text. CSS uppercases via tracking-wider+uppercase utility;
  // useTerm returns the base string. UI-SPEC §"Batch set strip" — section
  // legend is `{subdivision.upper}S` with the CSS doing the case transform.
  const sectionLegend = `${subdivision}s`;

  // Surface 2b — Duplicate clone. The strip owns useFieldArray, so it reads the
  // active row's CURRENT field values via getValues (the live edited values,
  // not the stale field snapshot) and appends a deep copy with a fresh row id.
  // useFieldArray generates a new field.id for the appended row automatically
  // (AUTHR-03 — never reuse the array index or the source row's id as key).
  const duplicateBatchSet = (index: number): void => {
    const source = getValues(`batchSets.${index}`) as Record<string, unknown> | undefined;
    if (source === undefined) return;
    // Deep-copy so the clone does not share object references (selections is a
    // nested record; a shallow copy would alias the source's selections).
    const clone =
      typeof structuredClone === "function"
        ? structuredClone(source)
        : (JSON.parse(JSON.stringify(source)) as Record<string, unknown>);
    // Drop the source's own `id` (if any) so RHF/the server issue a fresh one;
    // the field.id React key comes from useFieldArray's keyName regardless.
    // Bracket access — `clone` is a Record (index-signature) under
    // noPropertyAccessFromIndexSignature.
    delete clone["id"];
    append(clone);
  };

  return (
    <Fieldset legend={sectionLegend}>
      {/* Surface 2d — strip-level duplicate-name banner (warning tone). Renders
          as the FIRST child of the strip, above the card list, ONLY when a
          collision exists. role="alert" announces on appear; it does NOT steal
          focus. */}
      {duplicateNames ? (
        <div
          role="alert"
          className="border border-destructive bg-destructive/10 rounded-md p-3 flex items-start gap-3"
        >
          <AlertTriangle size={16} aria-hidden className="text-destructive shrink-0 mt-0.5" />
          <span className="text-sm text-[var(--recipe-ink)]">
            {`Two ${subdivision}s share the same name. Rename one so each ${subdivision} is distinct.`}
          </span>
        </div>
      ) : null}
      {fields.length === 0 ? (
        <EmptyState
          title={`Add a ${subdivision.toLowerCase()} to start composing this ${container.toLowerCase()}.`}
          cta={{
            label: `Add ${subdivision}`,
            onClick: () => append(makeEmptyBatchSet(fields.length)),
          }}
        />
      ) : (
        <>
          <SortableList
            items={fields.map((f) => ({ id: f.id }))}
            onReorder={(oldIdx, newIdx) => move(oldIdx, newIdx)}
          >
            <div className="flex flex-col gap-2">
              {fields.map((field, index) => (
                // field.id is RHF's stable per-row id (default keyName); we
                // use it both as React key (AUTHR-03) and as SortableItem id.
                <BatchSetCardRow
                  key={field.id}
                  stableId={field.id}
                  index={index}
                  subdivision={subdivision}
                  onRemove={() => remove(index)}
                  onDuplicate={() => duplicateBatchSet(index)}
                />
              ))}
            </div>
          </SortableList>
          <button
            type="button"
            onClick={() => append(makeEmptyBatchSet(fields.length))}
            className="self-start text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
          >
            + Add {subdivision}
          </button>
        </>
      )}
    </Fieldset>
  );
}
BatchSetStrip.displayName = "BatchSetStrip";

interface BatchSetCardRowProps {
  stableId: string;
  index: number;
  subdivision: string;
  onRemove: () => void;
  onDuplicate: () => void;
}

// Per-row wrapper so the drag-handle aria-label can interpolate the row's
// current name via useWatch without subscribing the whole strip. Mirrors the
// IngredientGroupRow pattern.
function BatchSetCardRow({
  stableId,
  index,
  subdivision,
  onRemove,
  onDuplicate,
}: BatchSetCardRowProps): React.ReactElement {
  const { control } = useFormContext();
  const nameValue = useWatch({
    control,
    name: `batchSets.${index}.name`,
  });
  const ariaName =
    typeof nameValue === "string" && nameValue.length > 0
      ? nameValue
      : `${subdivision} ${index + 1}`;
  return (
    <SortableItem id={stableId} dragHandleAriaLabel={`Reorder ${subdivision} ${ariaName}`}>
      <BatchSetCard index={index} onRemove={onRemove} onDuplicate={onDuplicate} />
    </SortableItem>
  );
}
